import styled, { keyframes } from 'styled-components'
import { TYPOGRAPHY } from '../../../../styles/theme'

// ── Chat Panel (always-rendered outer wrapper for scroll capture) ──

export const ChatPanel = styled.div`
  cursor: default;
  user-select: text;
`

// ── Chat Toggle (accordion header) ──

export const ChatToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-top: 1px solid var(--color-node-header-border);
  cursor: pointer;
  color: var(--color-text-muted);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: inherit;
  text-align: left;
  transition:
    background 0.15s,
    color 0.15s;
  border-radius: 0 0 6px 6px;

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-secondary);
  }
`

export const ChatToggleLabel = styled.span`
  flex: 1;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: 500;
  letter-spacing: 0.2px;
`

export const ChatToggleCount = styled.span`
  margin-left: 3px;
  font-weight: 400;
  opacity: 0.6;
`

export const ChatToggleUnreadBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #fff;
  background: var(--color-primary);
  border-radius: 4px;
  padding: 1px 5px;
  flex-shrink: 0;
`

export const ChatToggleTypingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
`

export const ChatToggleChevron = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.18s ease;
  transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
`

// ── Chat Container ──

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 400px;
  padding: 6px 12px 8px;
  cursor: default;
  user-select: text;
`

export const ChatHeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 2px;
`

export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  color: var(--color-text-muted);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-secondary);
    border-color: var(--color-border);
  }
`

// ── Message Area ──

export const MessageAreaWrapper = styled.div`
  position: relative;
  margin-top: 8px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

export const ScrollFadeTop = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: -4px;
  left: 0;
  right: 0;
  height: 33px;
  background: linear-gradient(to bottom, var(--color-node-bg-darker) 0%, transparent 100%);
  pointer-events: none;
  z-index: 2;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.2s ease;
`

export const MessageArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;
  }
`

export const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$isUser ? 'flex-end' : 'flex-start')};
  position: relative;
  z-index: 1;
`

export const MessageGroup = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$isUser ? 'flex-end' : 'flex-start')};
  max-width: 85%;
`

export const MessageBubble = styled.div<{ $isUser: boolean }>`
  width: fit-content;
  padding: 10px 14px;
  border-radius: ${(p) => (p.$isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px')};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap;
  cursor: text;
  user-select: text;
  background: ${(p) => (p.$isUser ? 'var(--color-primary)' : 'var(--color-chat-bubble-bg)')};
  color: ${(p) => (p.$isUser ? '#fff' : 'var(--color-text-primary)')};
  border: ${(p) => (p.$isUser ? 'none' : '1px solid var(--color-border)')};
`

export const MessageTimestamp = styled.div<{ $isUser: boolean }>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-muted);
  margin-top: 2px;
  padding: 0 4px;
  text-align: ${(p) => (p.$isUser ? 'right' : 'left')};
`

// ── Typing Indicator ──

const dotPulse = keyframes`
  0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
  30% { opacity: 1; transform: scale(1); }
`

export const TypingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 9px 14px;
  border-radius: 12px;
  background: var(--color-chat-bubble-bg);
  border: 1px solid var(--color-border);
  width: fit-content;
`

export const TypingDot = styled.span<{ $delay: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-text-muted);
  animation: ${dotPulse} 1.2s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay};
`

// ── Input Area ──

export const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-top: 6px;
`

export const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.4;
  font-family: inherit;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 5px 8px;
  max-height: 350px;
  min-height: 30px;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  background: var(--color-chat-input-bg);
  color: var(--color-text-primary);
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`

export const SendButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${(p) => (p.$disabled ? 'var(--color-border)' : 'var(--color-primary)')};
  color: #fff;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$disabled ? 'var(--color-border)' : 'var(--color-primary-hover)')};
  }
`
